/**
 * The eight cells of an order row, in the spec's order.
 *
 * Every one of them renders a field the server already decided. There is no branch in this
 * file that recomputes a payment state, picks an action or works out whether an order is
 * late: those live in `domain/rowState.ts` on the API, once, so the row, the rail count and
 * the KPI tile cannot tell three different stories about the same order.
 *
 * The hover detail on Items and Prints goes through `DefinitionPopover` rather than a
 * `title` attribute. A title is unreachable by keyboard and unread by most screen readers,
 * and the packer checking why a label has not printed is entitled to both.
 */
import type { PrintDoc } from '@elutide/shared';
import { Button, DefinitionPopover, Pill } from '@/components';
import { Menu, MenuItem } from './Menu';
import { countdown, formatCount, formatMoney, fullTimestamp, relativeAge } from '@/lib/format';
import {
  ATTENTION_LABEL,
  MATCHER_LABEL,
  PAYMENT_GLYPH,
  PAYMENT_STATE_LABEL,
  PAYMENT_STATE_TONE,
  QUICK_ACTION_LABEL,
  type OrdersPageRowView as OrdersPageRow,
} from './types';

/** The reprint menu, in the order the spec lists it. */
export const REPRINT_CHOICES: readonly { id: string; label: string; docs: PrintDoc[] }[] = [
  { id: 'slip', label: 'Packing slip', docs: ['packing_slip'] },
  { id: 'coa', label: 'Certificate', docs: ['coa'] },
  { id: 'label', label: 'Shipping label', docs: ['label'] },
  { id: 'all', label: 'All three', docs: ['packing_slip', 'coa', 'label'] },
];

export interface RowHandlers {
  /** The one contextual button. The row already carries which action it is. */
  onQuickAction: (row: OrdersPageRow) => void;
  /** Reprint from the row's own menu. One request, one Idempotency-Key. */
  onReprint: (row: OrdersPageRow, docs: readonly PrintDoc[]) => void;
  /** Which row's menu is open, so only one is ever open at a time. */
  openMenu: number | null;
  onToggleMenu: (orderId: number | null) => void;
  /** Inline Extend on a waiting order's countdown. */
  onExtend: (row: OrdersPageRow) => void;
  /** True while that order's extend is in flight, so the button can spin. */
  isExtending: (orderId: number) => boolean;
  /** True while that order's quick action is in flight. */
  isActing: (orderId: number) => boolean;
  /** Now, ticking, so a countdown moves without every cell owning a timer. */
  now: number;
}

/* ------------------------------------------------------------------ */
/* 1. Order                                                            */
/* ------------------------------------------------------------------ */

export function OrderCell({ row, now }: { row: OrdersPageRow; now: number }) {
  return (
    <div className="ord-cell ord-cell--order">
      <span className="ord-num" title={fullTimestamp(row.placed_at)}>
        {row.number}
      </span>
      <span className="ord-sub">
        <span>{relativeAge(row.placed_at, now)}</span>
        {row.flags.first_order ? <span className="ord-badge ord-badge--first">FIRST</span> : null}
        {row.flags.safearrival ? (
          <span className="ord-badge ord-badge--safe">SafeArrival</span>
        ) : null}
      </span>
      {row.attention_reasons.length > 0 ? (
        <span className="ord-reasons">
          {row.attention_reasons.map((reason) => (
            <span key={reason} className="ord-reason">
              {ATTENTION_LABEL[reason]}
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Customer                                                         */
/* ------------------------------------------------------------------ */

export function CustomerCell({ row }: { row: OrdersPageRow }) {
  const place = [row.customer.city, row.customer.state].filter(Boolean).join(', ');
  return (
    <div className="ord-cell">
      <span className="ord-name">{row.customer.name}</span>
      {place ? <span className="ord-sub">{place}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Payment                                                          */
/* ------------------------------------------------------------------ */

export function PaymentCell({
  row,
  handlers,
}: {
  row: OrdersPageRow;
  handlers: RowHandlers;
}) {
  const { payment } = row;
  const waiting = payment.state === 'waiting';
  const deadline = payment.autocancel_at;

  return (
    <div className="ord-cell ord-pay">
      <span className="ord-pay__line">
        {/*
          The method and its label are absent for a packer (redact.ts). The state chip is
          the packer's whole payment cell, with nothing in front of it: no glyph, no dash.
        */}
        {payment.method ? (
          <span className="ord-glyph" aria-hidden="true">
            {PAYMENT_GLYPH[payment.method]}
          </span>
        ) : null}
        {payment.method_label ? (
          <span className="ord-pay__label">{payment.method_label}</span>
        ) : null}
        <Pill tone={PAYMENT_STATE_TONE[payment.state]}>{PAYMENT_STATE_LABEL[payment.state]}</Pill>
      </span>
      <span className="ord-sub ord-pay__line">
        {payment.matcher ? (
          <span className="ord-badge ord-badge--matcher">{MATCHER_LABEL[payment.matcher]}</span>
        ) : null}
        {waiting && deadline ? (
          <>
            <span
              className="ord-countdown"
              data-late={Date.parse(deadline) <= handlers.now ? 'true' : 'false'}
              title={fullTimestamp(deadline)}
            >
              {Date.parse(deadline) <= handlers.now
                ? `overdue ${countdown(deadline, handlers.now)}`
                : `cancels in ${countdown(deadline, handlers.now)}`}
            </span>
            {payment.autocancel_extendable ? (
              <Button
                size="sm"
                variant="ghost"
                busy={handlers.isExtending(row.order_id)}
                onClick={(event) => {
                  event.stopPropagation();
                  handlers.onExtend(row);
                }}
              >
                Extend
              </Button>
            ) : null}
          </>
        ) : null}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 4. Total                                                            */
/* ------------------------------------------------------------------ */

/** A row with no `total_cents` is a packer's row. The cell renders nothing, not a dash. */
export function TotalCell({ row }: { row: OrdersPageRow }) {
  if (row.total_cents === undefined) return null;
  return (
    <div className="ord-cell ord-cell--right">
      <span className="ord-total">{formatMoney(row.total_cents)}</span>
      {row.refunded_cents !== undefined && row.refunded_cents > 0 ? (
        <span className="ord-sub ord-refunded">{formatMoney(row.refunded_cents)} refunded</span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 5. Items                                                            */
/* ------------------------------------------------------------------ */

/** The preview the server sends, as one sentence for the popover. */
export function itemsSummary(row: OrdersPageRow): string {
  if (row.items_preview.length === 0) return 'No lines on this order.';
  const listed = row.items_preview.map((line) => `${line.qty} x ${line.name}`).join(', ');
  const shown = row.items_preview.reduce((sum, line) => sum + line.qty, 0);
  return shown < row.item_count ? `${listed}, and ${row.item_count - shown} more` : listed;
}

export function ItemsCell({ row }: { row: OrdersPageRow }) {
  return (
    <DefinitionPopover definition={itemsSummary(row)} id={`items-${row.order_id}`}>
      <span className="ord-items" tabIndex={0}>
        {formatCount(row.item_count)}
      </span>
    </DefinitionPopover>
  );
}

/* ------------------------------------------------------------------ */
/* 6. Prints                                                           */
/* ------------------------------------------------------------------ */

const PRINT_SQUARES: readonly { doc: PrintDoc; short: string; label: string }[] = [
  { doc: 'packing_slip', short: 'S', label: 'Packing slip' },
  { doc: 'coa', short: 'C', label: 'Certificate' },
  { doc: 'label', short: 'L', label: 'Shipping label' },
];

/** Three acks, in words, for the popover. Filled means the agent confirmed it printed. */
export function printsSummary(row: OrdersPageRow): string {
  const at: Readonly<Record<PrintDoc, string | null>> = {
    packing_slip: row.prints.slip_at,
    coa: row.prints.coa_at,
    label: row.prints.label_at,
  };
  return PRINT_SQUARES.map((square) =>
    at[square.doc]
      ? `${square.label} ${fullTimestamp(at[square.doc])}`
      : `${square.label} not printed`,
  ).join('. ');
}

export function PrintsCell({ row }: { row: OrdersPageRow }) {
  const at: Readonly<Record<PrintDoc, string | null>> = {
    packing_slip: row.prints.slip_at,
    coa: row.prints.coa_at,
    label: row.prints.label_at,
  };
  const done = PRINT_SQUARES.filter((square) => at[square.doc] !== null).length;

  return (
    <DefinitionPopover definition={printsSummary(row)} id={`prints-${row.order_id}`}>
      <span className="ord-prints" tabIndex={0} aria-label={`${done} of 3 documents printed`}>
        {PRINT_SQUARES.map((square) => (
          <span
            key={square.doc}
            className="ord-print"
            data-done={at[square.doc] ? 'true' : 'false'}
            aria-hidden="true"
          >
            {/*
              The letter is the document and the shape is the state: a solid border and a
              filled ground for an ack, a dashed border and a hollow one for a document that
              has not printed. DESIGN.md section 7: colour is never the only signal, and the
              person this cell is for is a packer deciding whether to reprint.
            */}
            {square.short}
          </span>
        ))}
      </span>
    </DefinitionPopover>
  );
}

/* ------------------------------------------------------------------ */
/* 7. Fulfilment                                                       */
/* ------------------------------------------------------------------ */

const FULFILMENT_LABEL: Readonly<Record<string, string>> = {
  awaiting_payment: 'Awaiting payment',
  to_print: 'To print',
  to_pack: 'Printed',
  packed: 'Packed',
  in_transit: 'In transit',
  delivered: 'Delivered',
  needs_attention: 'Needs attention',
  exceptions: 'Exception',
  all: 'Open',
  other: 'Closed',
};

const FULFILMENT_TONE: Readonly<Record<string, 'neutral' | 'ok' | 'warn' | 'crit' | 'info'>> = {
  awaiting_payment: 'warn',
  to_print: 'info',
  to_pack: 'info',
  packed: 'info',
  in_transit: 'info',
  delivered: 'ok',
  needs_attention: 'crit',
  exceptions: 'crit',
  all: 'neutral',
  other: 'neutral',
};

/**
 * A tracking URL is only followed when it is https.
 *
 * The server builds these from a fixed host table and there is no path today by which a
 * `javascript:` or `data:` URL could reach the field. That is a property of today's
 * producer, not of the contract: `tracking_url` is a nullable string on the wire, and an
 * href taken from a payload without a scheme check is one repo change from being a hole.
 */
export function safeTrackingUrl(url: string | null | undefined): string | null {
  return url && url.startsWith('https://') ? url : null;
}

export function FulfilmentCell({ row }: { row: OrdersPageRow }) {
  const { fulfilment } = row;
  const state = fulfilment.state;
  const label =
    state === 'packed' && fulfilment.photo_count > 0
      ? `Packed, ${fulfilment.photo_count} photo${fulfilment.photo_count === 1 ? '' : 's'}`
      : (FULFILMENT_LABEL[state] ?? row.ops_state);

  return (
    <div className="ord-cell">
      <Pill tone={FULFILMENT_TONE[state] ?? 'neutral'}>{label}</Pill>
      {fulfilment.tracking ? (
        <span className="ord-sub">
          {safeTrackingUrl(fulfilment.tracking_url) ? (
            <a
              className="ord-track"
              href={safeTrackingUrl(fulfilment.tracking_url) as string}
              target="_blank"
              rel="noreferrer noopener"
              onClick={(event) => event.stopPropagation()}
            >
              {(fulfilment.carrier ?? 'carrier').toUpperCase()} {fulfilment.tracking}
            </a>
          ) : (
            <span className="o-mono">
              {(fulfilment.carrier ?? 'carrier').toUpperCase()} {fulfilment.tracking}
            </span>
          )}
        </span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 8. Action                                                           */
/* ------------------------------------------------------------------ */

export function ActionCell({ row, handlers }: { row: OrdersPageRow; handlers: RowHandlers }) {
  const action = row.quick_action;

  /**
   * Reprint is a menu, not a button.
   *
   * Spec 02-orders asks for slip, COA, label or all, and the difference matters: a label
   * reprint costs nothing but a certificate reprint clears the COA ack and reprints a
   * document with a lot number on it. Guessing which one was meant is not a saving.
   */
  if (action === 'reprint') {
    return (
      <div className="ord-cell ord-cell--right">
        <Menu
          label="Reprint"
          align="right"
          busy={handlers.isActing(row.order_id)}
          open={handlers.openMenu === row.order_id}
          onToggle={() => handlers.onToggleMenu(row.order_id)}
          onClose={() => handlers.onToggleMenu(null)}
        >
          {REPRINT_CHOICES.map((choice) => (
            <MenuItem
              key={choice.id}
              label={choice.label}
              onSelect={() => {
                handlers.onToggleMenu(null);
                handlers.onReprint(row, choice.docs);
              }}
            />
          ))}
        </Menu>
      </div>
    );
  }

  return (
    <div className="ord-cell ord-cell--right">
      <Button
        size="sm"
        variant={action === 'mark_paid' || action === 'print' ? 'primary' : 'default'}
        busy={handlers.isActing(row.order_id)}
        onClick={(event) => {
          event.stopPropagation();
          handlers.onQuickAction(row);
        }}
      >
        {QUICK_ACTION_LABEL[action]}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The unmatched payment row                                           */
/* ------------------------------------------------------------------ */

